import React from "react";
import { useNavigate } from "react-router-dom";

import logger from '../../../../utils/logger';

// Utility classes for consistent styling (static parts only—no bg-gradient)
const cardClass = `
  relative
  transition-all duration-200 
  hover:shadow-lg dark:shadow-none
  cursor-pointer 
  rounded-lg
  group
  overflow-hidden
  h-full
  shadow-sm
  border border-gray-100 dark:border-gray-800
  hover:border-gray-200 dark:hover:border-gray-700
  hover:translate-y-[-1px]
`;

const iconContainerClass = `
  w-8 h-8
  mb-2
  relative 
  flex items-center justify-center
  transition-all duration-200
  bg-transparent
  overflow-visible
`;

const iconClass = `
  w-6 h-6
  object-contain 
  object-center
  group-hover:scale-110 
  transition-transform duration-200
  mix-blend-multiply
`;

const labelClass = `
  text-xs
  font-medium
  text-gray-700 dark:text-black
  text-center 
  group-hover:text-[#029831] 
  transition-colors
  line-clamp-2
`;

const notificationBadgeClass = `
  absolute
  top-1
  right-1
  flex
  items-center
  justify-center
  min-w-[14px]
  h-[14px]
  px-1
  text-[10px]
  font-medium
  text-white
  rounded-full
  shadow-sm
  transform
  group-hover:scale-110
  transition-transform duration-200
  z-10
`;

const QuickActions = ({ actions: providedActions, onCustomize }) => {
  const navigate = useNavigate();

  // Prioritize certain actions
  const priorityOrder = [
    "client-database",
    "email",
    "messages",
    "events",
    "sales-coach",
  ];
  const actions = [
    ...priorityOrder
      .map((id) => providedActions.find((a) => a.id === id))
      .filter(Boolean),
    ...providedActions.filter((a) => !priorityOrder.includes(a.id)),
  ];

  const getIconPath = (id) => {
    const iconMap = {
      "client-database": "/images/HomeDashboard/client_database.png",
      email: "/images/HomeDashboard/email.png",
      messages: "/images/HomeDashboard/messages.png",
      events: "/images/HomeDashboard/events.png",
      "sales-coach": "/images/HomeDashboard/sales_coach.png",
      "market-news": "/images/HomeDashboard/market_news.png",
      "market-research": "/images/HomeDashboard/market_research.png",
      research: "/images/HomeDashboard/market_research.png",
      allocations: "/images/HomeDashboard/allocations.png",
      "financial-news": "/images/HomeDashboard/financial_news.png",
      compliance: "/images/HomeDashboard/compliance.png",
    };

    if (id === "market-research" || id === "research") {
      logger.info("Market Research Icon Request:", {
        requestedId: id,
        mappedPath: iconMap[id],
        allAvailableIds: Object.keys(iconMap),
      });
    }

    return iconMap[id] || iconMap["client-database"];
  };

  const getIconStyle = (id) => {
    const baseStyle = {
      willChange: "transform",
      transform: "scale(1)",
      maxWidth: "100%",
      maxHeight: "100%",
      filter: "brightness(1) contrast(1.1)",
    };

    const extraLargeIcons = ["events", "market-news", "financial-news"];
    if (extraLargeIcons.includes(id)) {
      return { ...baseStyle, transform: "scale(1.25)", maxWidth: "110%", maxHeight: "110%" };
    }

    const largerIcons = ["messages", "sales-coach"];
    if (largerIcons.includes(id)) {
      return { ...baseStyle, transform: "scale(1.15)", maxWidth: "105%", maxHeight: "105%" };
    }

    if (id === "messages") {
      return {
        ...baseStyle,
        transform: "scale(1.15)",
        maxWidth: "105%",
        maxHeight: "105%",
        filter: "brightness(1) contrast(1.1) grayscale(1)",
        opacity: "0.85",
      };
    }

    return baseStyle;
  };

  const handleActionClick = (route) => {
    if (route.startsWith("/")) navigate(route);
    else navigate(`/${route}`);
  };

  const calculateFillPercentage = (count, type) => {
    if (count === 0) return 100;
    let deduction;
    switch (type) {
      case "urgent": deduction = count * 10; break;
      case "soon": deduction = count * 8; break;
      case "upcoming": deduction = count * 6; break;
      case "future": deduction = count * 4; break;
      default: deduction = 0;
    }
    return Math.max(0, 100 - deduction);
  };

  const getNotificationType = (action) => {
    if (action.id === "email" && action.count >= 7) return "urgent";
    if (action.id === "messages" && action.count >= 3) return "soon";
    if (action.count > 5) return "urgent";
    if (action.count > 3) return "soon";
    if (action.count > 1) return "upcoming";
    return action.count === 0 ? "none" : "future";
  };

  const getStatusColors = (type) => {
    switch (type) {
      case "urgent":
        return {
          gradient: "from-red-600 to-red-700",
          badge: "bg-red-600",
          background: "from-red-100/40 to-red-50/40",
        };
      case "soon":
        return {
          gradient: "from-amber-600 to-amber-700",
          badge: "bg-amber-600",
          background: "from-amber-100/40 to-amber-50/40",
        };
      case "upcoming":
        return {
          gradient: "from-yellow-600 to-yellow-700",
          badge: "bg-yellow-600",
          background: "from-yellow-100/40 to-yellow-50/40",
        };
      case "future":
        return {
          gradient: "from-emerald-500 to-emerald-600",
          badge: "bg-emerald-500",
          background: "from-emerald-100/40 to-emerald-50/40",
        };
      default:
        return {
          gradient: "from-emerald-600 to-emerald-700",
          badge: "bg-emerald-600",
          background: "from-emerald-100/40 to-emerald-50/40",
        };
    }
  };

  const handleImageError = (e) => {
    logger.error("Image failed to load:", {
      src: e.target.src,
      alt: e.target.alt,
      id: e.target.dataset.id,
    });
    e.target.src = "/images/HomeDashboard/client_database.png";
  };

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 h-full"
      role="menu"
      aria-label="Quick actions"
    >
      {actions.map((action) => {
        const type = getNotificationType(action);
        const colors = getStatusColors(type);
        const fill = calculateFillPercentage(action.count, type);

        return (
          <div
            key={action.id}
            onClick={() => handleActionClick(action.route)}
            className={`
              ${cardClass}
              bg-gradient-to-br ${colors.background}
              transition-opacity duration-200 group-hover:opacity-60
            `}
            role="menuitem"
            tabIndex={0}
            aria-label={`${action.label}${action.count ? `, ${action.count} notifications` : ""}`}
            onKeyPress={(e) => {
              if (e.key === "Enter" || e.key === " ") handleActionClick(action.route);
            }}
          >
            {action.count > 0 && (
              <span className={`${notificationBadgeClass} ${colors.badge}`}>
                {action.count}
              </span>
            )}

            <div className="relative flex flex-col items-center justify-center h-full py-2.5 px-2">
              <div className={iconContainerClass}>
                <img
                  src={getIconPath(action.id)}
                  alt={action.label}
                  className={iconClass}
                  style={getIconStyle(action.id)}
                  onError={handleImageError}
                  loading="lazy"
                  data-id={action.id}
                />
              </div>
              <span className={labelClass}>{action.label}</span>
            </div>

            <div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-100"
              role="progressbar"
              aria-valuenow={fill}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div
                className={`
                  h-full 
                  bg-gradient-to-r ${colors.gradient}
                  transition-all duration-300
                  rounded-r-full
                `}
                style={{ width: `${fill}%`, willChange: "width" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default QuickActions;
